import { mapErrorCodeToCopyKey } from "../../@api/apiError";
import type { ApiErrorResponse } from "../../@api/apiError";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { signupUserOtpPost, signupUserPost } from "../../@api/signupUser";
import type { History } from "history";
import {
  getIsAuthCodeRequested,
  getIsWeb,
  getRedirectState,
  getRedirectUri,
} from "../../@api/config";
import { passwordUpdatePut } from "../../@api/updatePassword";
import { routes } from "../../utils/routes";
import {
  getUserCompletion,
  requestEmailOtpV3,
  requestSmsOtpV3,
  setUserCompletion,
  updatePhoneNumberV3,
  updateUserEmailV3,
} from "../../utils/completion";
import type { BECompletionV4 } from "@kivra/sdk/types/core/completion";
import type { RootState } from "../rootReducer";
import { getLang } from "../../bootstrap/copy";
import { redirect } from "../../utils/location";
import { authorizeGet } from "../../@api/authorize";
import { getSignupLoginValues } from "../signup/signupGetters";
import { getHideNavigation } from "../hideNavigation/hideNavigation";
import {
  getAccessToken,
  getNewPassword,
  getPhoneNumber,
  getResourceOwner,
  getTokenType,
  getTunnistusData,
  getUserIdFromResourceOwner,
  getUsername,
  setCompletionFetchState,
  setCompletionOTPState,
  setCompletions,
  setErrorCopyKey,
  setVerificationValues,
} from ".";
import type { AccessTokenResponse } from "../../@api/signupUser";

interface ThunkOptions {
  state: RootState;
  extra: {
    history: History;
  };
}

export const signupUser = createAsyncThunk<void, undefined, ThunkOptions>(
  "signup/postUser",
  async (_, { getState, dispatch, extra }) => {
    try {
      const state = getState();
      const isMobile = getHideNavigation() || !getIsWeb();
      const response: AccessTokenResponse = await signupUserPost(
        getUsername(state),
        getPhoneNumber(state),
        getLang(),
        getTunnistusData(state),
      );
      const data = getTunnistusData(state);
      dispatch(
        setVerificationValues({
          ...data,
          accessToken: response.access_token,
          resourceOwner: response.resource_owner,
        }),
      );
      if (isMobile) {
        dispatch(authRedirect(response));
      } else {
        extra.history.push(routes.VERIFICATIONS);
      }
    } catch (error) {
      const codeError = error as ApiErrorResponse;
      dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
    }
  },
);

export const oldSignupUserOtp = createAsyncThunk<void, string, ThunkOptions>(
  "signup/postUserOtp",
  async (otp, { getState, dispatch }) => {
    try {
      const state = getState();
      const response = await signupUserOtpPost(
        getUsername(state),
        getPhoneNumber(state),
        getLang(),
        getTunnistusData(state),
        otp,
      );
      dispatch(authRedirect(response));
    } catch (error) {
      const codeError = error as ApiErrorResponse;
      dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
    }
  },
);

// TODO: Maybe rename this to indicate that it also sets the values to the state
export const fetchUserCompletion = createAsyncThunk<
  void,
  undefined,
  ThunkOptions
>("signup/postUserCompletion", async (_, { getState, dispatch }) => {
  try {
    const state = getState();
    dispatch(setCompletionFetchState("FETCHING"));
    const key = getUserIdFromResourceOwner(getResourceOwner(state));
    const completionResponse = await getUserCompletion(
      key,
      getAccessToken(state),
    );
    dispatch(setCompletions(completionResponse.completions));
    dispatch(setCompletionFetchState("FETCHED"));
  } catch (error) {
    const codeError = error as ApiErrorResponse;
    dispatch(setCompletionFetchState("ERROR"));
    dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
  }
});

// TODO: At the moment this is only used for postponing completions
export const userCompletion = createAsyncThunk<
  void,
  & Pick<BECompletionV4, "context" | "type">
  & ({ action: "postpone" } | { action: "confirm"; confirmed: string }),
  ThunkOptions
>("signup/postUserCompletion", async (completion, { getState, dispatch }) => {
  try {
    const state = getState();
    const key = getUserIdFromResourceOwner(getResourceOwner(state));
    await setUserCompletion(key, getAccessToken(state), completion);
  } catch (error) {
    const codeError = error as ApiErrorResponse;
    dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
  }
});

export const requestSmsOtp = createAsyncThunk<void, string, ThunkOptions>(
  "signup/requestUserOtp",
  async (phoneNumber, { getState, dispatch }) => {
    try {
      const state = getState();
      const key = getUserIdFromResourceOwner(getResourceOwner(state));
      await requestSmsOtpV3(key, getAccessToken(state), phoneNumber);
    } catch (error) {
      const codeError = error as ApiErrorResponse;
      dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
    }
  },
);

export const requestEmailOtp = createAsyncThunk<void, string, ThunkOptions>(
  "signup/requestUserOtp",
  async (email, { getState, dispatch }) => {
    try {
      const state = getState();
      const key = getUserIdFromResourceOwner(getResourceOwner(state));
      await requestEmailOtpV3(key, getAccessToken(state), email);
    } catch (error) {
      const codeError = error as ApiErrorResponse;
      dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
    }
  },
);

interface UserDataUpdate {
  data: string;
  otp: string;
  context: BECompletionV4["context"];
}

export const requestChangePhone = createAsyncThunk<
  void,
  UserDataUpdate,
  ThunkOptions
>("signup/requestChangePhone", async (data, { getState, dispatch }) => {
  try {
    const state = getState();
    const key = getUserIdFromResourceOwner(getResourceOwner(state));
    dispatch(setCompletionOTPState("SETTING"));
    await updatePhoneNumberV3(
      key,
      getAccessToken(state),
      data.data,
      data.otp,
      data.context,
    );
    dispatch(setCompletionOTPState("SET"));
  } catch (error) {
    const codeError = error as ApiErrorResponse;
    dispatch(setCompletionOTPState("ERROR"));
    dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
  }
});
export const requestChangeEmail = createAsyncThunk<
  void,
  UserDataUpdate,
  ThunkOptions
>("signup/requestChangePhone", async (data, { getState, dispatch }) => {
  try {
    const state = getState();
    const key = getUserIdFromResourceOwner(getResourceOwner(state));
    dispatch(setCompletionOTPState("SETTING"));
    await updateUserEmailV3(
      key,
      getAccessToken(state),
      data.data,
      data.otp,
      data.context,
    );
    dispatch(setCompletionOTPState("SET"));
  } catch (error) {
    const codeError = error as ApiErrorResponse;
    dispatch(setCompletionOTPState("ERROR"));
    dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
  }
});

export const authRedirect = createAsyncThunk<
  void,
  AccessTokenResponse,
  ThunkOptions
>("signup/userAuthRedirect", async (response, { dispatch }) => {
  if (getIsAuthCodeRequested()) {
    dispatch(authAppRedirect(response));
    return;
  }
  const accessToken = response.access_token;
  const redirectState = response.state || getRedirectState();
  const redirectUri = getRedirectUri();
  redirect(
    redirectUri +
      `#access_token=${accessToken}` +
      `&state=${redirectState}`,
  );
});

export const authAppRedirect = createAsyncThunk<
  void,
  AccessTokenResponse,
  ThunkOptions
>("signup/userAuthAppRedirect", async (response) => {
  const { redirect_uri } = await authorizeGet(
    getUserIdFromResourceOwner(response.resource_owner),
    response.access_token,
  );
  redirect(redirect_uri);
});

export const passwordUpdate = createAsyncThunk<void, undefined, ThunkOptions>(
  "signup/resetPassword",
  async (_, { getState, dispatch }) => {
    try {
      const state = getState();
      const key = getUserIdFromResourceOwner(getResourceOwner(state));
      await passwordUpdatePut(
        getNewPassword(state),
        key,
        getAccessToken(state),
        getTokenType(state),
      );
      dispatch(authRedirect(getSignupLoginValues(state)));
    } catch (error) {
      const codeError = error as ApiErrorResponse;
      dispatch(setErrorCopyKey(mapErrorCodeToCopyKey(codeError.code)));
    }
  },
);
